<div class="image-upload">
  <!-- Label (optional) -->
  <h4 *ngIf="label" class="upload-label">{{ label | translate }}</h4>

  <!-- Camera Capture Modal -->
  <div *ngIf="showCamera" class="camera-modal-overlay">
    <div class="camera-modal">
      <app-camera-capture
        [label]="label || 'Capture Document'"
        (photoCaptured)="onPhotoCaptured($event)"
        (cancelled)="closeCamera()"
      ></app-camera-capture>
    </div>
  </div>

  <!-- Drop Zone (when no file) -->
  <div
    class="no-upload"
    *ngIf="!fileSrc && !showCamera"
    [class.dragging]="isDragging"
    [class.has-error]="hasErrors"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"
  >
    <div class="upload-icon-wrapper">
      <mat-icon class="upload-icon">{{
        isDragging ? "file_download" : "cloud_upload"
      }}</mat-icon>
    </div>
    <h4 class="upload-title">
      {{
        (isDragging ? "Drop your file here" : title || "Upload a file")
          | translate
      }}
    </h4>
    <p class="upload-hint">
      {{
        (isDragging ? "Release to upload" : "Click to browse or drag and drop")
          | translate
      }}
    </p>
    <p class="upload-formats">
      {{ "Accepted formats" | translate }}: JPEG{{
        acceptedFormats.includes("image/png") ? ", PNG" : ""
      }}{{ enablePdfPreview ? ", PDF" : "" }} • {{ "Max" | translate }}
      {{ maxFileSizeMB }}MB
    </p>

    <!-- Upload Button -->
    <button
      *ngIf="!isUploading"
      type="button"
      class="upload-btn"
      (click)="onUploadButtonClick(); $event.stopPropagation()"
    >
      <mat-icon>cloud_upload</mat-icon>
      {{ "Upload" | translate }}
    </button>

    <!-- Camera Loading (while checking support) -->
    <div *ngIf="isCheckingCamera && !isUploading" class="camera-loading">
      <mat-spinner diameter="20"></mat-spinner>
      <span>{{ "CheckingCamera..." | translate }}</span>
    </div>

    <!-- Divider "ou" -->
    <div
      *ngIf="hasCameraSupport && !isUploading && !isCheckingCamera"
      class="upload-divider"
    >
      <span>{{ "or" | translate }}</span>
    </div>

    <!-- Camera Button (when supported) -->
    <button
      *ngIf="hasCameraSupport && !isUploading && !isCheckingCamera"
      type="button"
      class="camera-btn"
      (click)="openCamera(); $event.stopPropagation()"
    >
      <mat-icon>photo_camera</mat-icon>
      {{ "takePhoto" | translate }}
    </button>

    <!-- Loading indicator -->
    <div *ngIf="isUploading" class="upload-loading">
      <mat-spinner diameter="24"></mat-spinner>
      <span>{{ "Processing file..." | translate }}</span>
    </div>
  </div>

  <!-- File Preview (when file uploaded) -->
  <div
    class="has-upload"
    *ngIf="!!fileSrc"
    [class.vertical]="deleteButtonDisposition === 'vertical'"
    [class.horizontal]="deleteButtonDisposition === 'horizontal'"
  >
    <!-- Image Preview -->
    <div *ngIf="!isPdf" class="preview-content">
      <div class="preview-wrapper">
        <img
          [style.maxHeight]="maxHeight || 'auto'"
          [src]="fileSrc"
          alt="Document preview"
        />
      </div>
      <div class="file-info">
        <div class="file-details">
          <mat-icon>image</mat-icon>
          <div class="file-meta">
            <span
              class="file-name"
              [matTooltip]="fileName"
              [matTooltipDisabled]="!fileName"
            >
              {{ fileName }}
            </span>
            <span class="file-size">{{ fileSize }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- PDF Preview -->
    <div *ngIf="isPdf" class="preview-content pdf-preview">
      <div class="pdf-icon-wrapper">
        <mat-icon class="pdf-icon">picture_as_pdf</mat-icon>
      </div>
      <div class="file-info">
        <div class="file-details">
          <mat-icon>description</mat-icon>
          <div class="file-meta">
            <span
              class="file-name"
              [matTooltip]="fileName"
              [matTooltipDisabled]="!fileName"
            >
              {{ fileName }}
            </span>
            <span class="file-size">{{ fileSize }}</span>
          </div>
        </div>
      </div>
      <!-- Full PDF Viewer (optional, shown below) -->
      <div class="pdf-viewer-wrapper" *ngIf="enablePdfPreview">
        <pdf-viewer
          [src]="fileSrc"
          [render-text]="true"
          [original-size]="false"
          [show-all]="false"
          [fit-to-page]="true"
        ></pdf-viewer>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <h3 class="upload-success-title" *ngIf="hasTitleDeleteImg">
        <mat-icon class="success-icon">check_circle</mat-icon>
        {{ "File uploaded successfully" | translate }}
      </h3>

      <div class="button-group">
        <button
          class="action-btn replace-btn"
          type="button"
          (click)="replaceFile()"
        >
          {{ "Replace" | translate }}
        </button>
        <button
          class="action-btn remove-btn"
          type="button"
          (click)="removeFile()"
        >
          {{ "Remove" | translate }}
        </button>
      </div>
    </div>
  </div>

  <!-- Validation Messages -->
  <div
    *ngIf="showValidationMessages && validationResult"
    class="validation-messages"
  >
    <!-- Errors -->
    <div *ngIf="hasErrors" class="error-messages">
      <div
        *ngFor="let error of validationResult.errors"
        class="message error-message"
      >
        <mat-icon>error</mat-icon>
        <span>{{ error.message | translate }}</span>
      </div>
    </div>

    <!-- Warnings -->
    <div *ngIf="hasWarnings" class="warning-messages">
      <div
        *ngFor="let warning of validationResult.warnings"
        class="message warning-message"
      >
        <mat-icon>warning</mat-icon>
        <span>{{ warning.message | translate }}</span>
      </div>
    </div>
  </div>

  <!-- Hidden File Input -->
  <input
    type="file"
    class="upload-input"
    #inputfile
    (change)="onFileChange($event)"
    [accept]="expandedAcceptFormats"
  />
</div>
